import { SearchOutlined } from "@mui/icons-material";
import { Box, Grid } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ReusableTable from "../../common/table/CommonTable";
import CustomTextField from "../../common/textfield/CommonTextField";
import { fetchHistoryPatients } from "../../redux/patient/patientAction";

const HistoryList = () => {
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const searchTimeoutRef = useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const historyState = useSelector(
    (state) => state?.patients?.historyPatient || {}
  );

  const historyData = historyState?.data?.data || [];
  const totalRecords = historyState?.data?.totalRecords || 0;
  const totalPages = Math.ceil(totalRecords / 10);

  useEffect(() => {
  const fetchData = () => {
    const payload = {
      pageNo: currentPage,
      pagesize: 10,
      pagination_required: true,
      search: searchQuery,
    };
    dispatch(fetchHistoryPatients(payload));
  };

    fetchData();
  }, [currentPage, searchQuery, dispatch]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Enhanced search with debouncing
  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchInputValue(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setSearchQuery(value);
      setCurrentPage(1); // Reset to first page when searching
    }, 1000);
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return "N/A";
    const date = new Date(dateTimeString.split("-").reverse().join("-"));
    return date.toLocaleString("en-GB", { hour12: true });
  };

  // Transform data and filter based on search query
  const filteredData = Array.isArray(historyData)
    ? historyData
        .map((item) => ({
          id: item.id,
          serialNumber: item.Patient?.patient_serial_no || "Unknown",
          patientName: item.Patient?.patient_name || "Unknown",
          attendantName: item.Attender?.attender_name || "Unknown",
          status: item.attender_status === 2 ? "In" : "Out",
          block: item.Block?.block_name || "Unknown",
          floor: item.Floor?.floor_name || "Unknown",
          ward: item.Ward?.ward_name || "Unknown",
          inTime: formatDateTime(item.in_time),
          outTime: formatDateTime(item.out_time),
        }))
        .filter((record) => {
          if (!searchQuery) return true;
          const query = searchQuery.toLowerCase();
          return (
            record.patientName.toLowerCase().includes(query) ||
            record.attendantName.toLowerCase().includes(query) ||
            record.block.toLowerCase().includes(query) ||
            record.floor.toLowerCase().includes(query) ||
            record.ward.toLowerCase().includes(query) ||
            record.serialNumber.toLowerCase().includes(query) ||
            record.status.toLowerCase().includes(query)
          );
        })
    : [];

  const columns = [
    { id: "serialNumber", label: "ID", sortable: true, width: "60px" },
    { id: "patientName", label: "Patient Name", sortable: true },
    { id: "attendantName", label: "Attendant Name", sortable: true },
    {
      id: "status",
      label: "In/Out",
      sortable: true,
      width: "80px",
      render: (status) => (
        <Box
          sx={{
            backgroundColor: status === "In" ? "green" : "red",
            borderRadius: 1,
            px: 1,
            py: 0.5,
            display: "inline-block",
            color: "white",
          }}
        >
          {status}
        </Box>
      ),
    },
    { id: "block", label: "Block", sortable: true },
    { id: "floor", label: "Floor", sortable: true },
    { id: "ward", label: "Ward", sortable: true },
    { id: "inTime", label: "In Time", sortable: true },
    { id: "outTime", label: "Out Time", sortable: true },
  ];

  // Cleanup timeout on component unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Box>
      <Grid container spacing={2} mt={3}>
        <Grid item xs={12} md={6}></Grid>
        <Grid item xs={12} md={6}>
          <CustomTextField
            label="Search by Patient, Attendant, Block, Floor, Ward..."
            variant="outlined"
            value={searchInputValue}
            onChange={handleSearchChange}
            sx={{ mb: 3 }}
            placeholder="Type to search..."
            InputProps={{
              startAdornment: (
                <SearchOutlined
                  sx={{ color: "action.active", mr: 1, my: 0.5 }}
                />
              ),
            }}
          />
        </Grid>
      </Grid>

      <ReusableTable
        title={`Attendant History${
          searchQuery ? ` (Filtered by: ${searchQuery})` : ""
        }`}
        columns={columns}
        data={filteredData}
        isLoading={historyState.inProgress}
        headerColor="#000"
        rowColor="#ffffff"
        alternateRowColor="#f5f5f5"
        withPagination={true}
        rowsPerPage={10}
        totalPages={totalPages}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        withActions={false}
        showDownloadOption={true}
      />
    </Box>
  );
};

export default HistoryList;
